import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import { doc, getDoc, addDoc, collection, Timestamp, query, where, getDocs } from "firebase/firestore";
import KioskRegister from "./KioskRegister";

function Kiosk({ token }) {
  const [phase, setPhase] = useState("loading");
  const [kioskUser, setKioskUser] = useState(null);
  const [todayRecords, setTodayRecords] = useState([]);
  const [punching, setPunching] = useState(false);
  const [message, setMessage] = useState("");
  const [lastStatus, setLastStatus] = useState(null);
  const [companyId, setCompanyId] = useState(null);

  useEffect(() => { validateTokenAndLoad(); }, [token]);

  const validateTokenAndLoad = async () => {
    const params = new URLSearchParams(window.location.search);
    const cid = params.get("cid");
    setCompanyId(cid);

    try {
      const tokenData = JSON.parse(atob(token));
      if (Date.now() - tokenData.t > 5 * 60 * 1000) {
        setPhase("invalid"); return;
      }
    } catch {
      setPhase("invalid"); return;
    }

    const savedPhone = localStorage.getItem("miyabi_kiosk_phone");
    const cachedName = localStorage.getItem("miyabi_kiosk_name");
    const cachedUid = localStorage.getItem("miyabi_kiosk_uid");
    if (!savedPhone) { setPhase("register"); return; }

    // Firestoreから最新情報を取得
    try {
      const snap = await getDoc(doc(db, "kioskUsers", savedPhone));
      if (!snap.exists()) {
        localStorage.removeItem("miyabi_kiosk_status");
        setPhase("register"); return;
      }
      const userData = snap.data();
      setKioskUser(userData);
      localStorage.setItem("miyabi_kiosk_status", userData.status);
      localStorage.setItem("miyabi_kiosk_uid", userData.uid || "");
      if (userData.status === "pending") { setPhase("pending"); return; }
      await fetchTodayRecords(userData.uid || cachedUid);
      setPhase("punch");
    } catch {
      // Firestoreアクセス失敗時はキャッシュで代替
      const cachedStatus = localStorage.getItem("miyabi_kiosk_status");
      if (cachedStatus === "approved" && cachedUid) {
        setKioskUser({ name: cachedName, phone: savedPhone, uid: cachedUid, status: "approved" });
        await fetchTodayRecords(cachedUid);
        setPhase("punch");
      } else {
        setPhase("register");
      }
    }
  };

  // attendanceコレクションからuidで本日の打刻を取得
  const fetchTodayRecords = async (uid) => {
    if (!uid) return;
    try {
      const q = query(collection(db, "attendance"), where("uid", "==", uid));
      const snap = await getDocs(q);
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const data = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(d => d.timestamp?.toDate() >= today)
        .sort((a, b) => b.timestamp - a.timestamp);
      setTodayRecords(data);
      if (data.length > 0) setLastStatus(data[0].type);
    } catch (e) {
      console.error("fetchTodayRecords error:", e);
    }
  };

  const handlePunch = async (type) => {
    setPunching(true);
    try {
      const uid = kioskUser.uid || localStorage.getItem("miyabi_kiosk_uid");
      // attendanceコレクションに統一して保存（PCログイン打刻と同じ場所）
      await addDoc(collection(db, "attendance"), {
        uid,
        email: kioskUser.phone, // キオスクユーザーはメールなし、電話番号で代替
        name: kioskUser.name,
        companyId: companyId || kioskUser.companyId || "",
        type,
        source: "kiosk",
        timestamp: Timestamp.now(),
      });
      setLastStatus(type);
      setMessage(`${kioskUser.name}さん、${type}しました！`);
      setPhase("done");
    } catch (e) {
      setMessage("エラーが発生しました: " + e.message);
    }
    setPunching(false);
  };

  const fmt = (ts) => ts?.toDate().toLocaleTimeString("ja-JP", { hour: "2-digit", minute: "2-digit" });

  if (phase === "invalid") return (
    <div style={st.container}><div style={st.box}>
      <div style={{ fontSize: "48px", textAlign: "center", marginBottom: "12px" }}>⚠️</div>
      <h2 style={st.title}>QRコードの有効期限が切れています</h2>
      <p style={st.desc}>会社のPCに表示されている最新のQRコードを読み取ってください。</p>
    </div></div>
  );

  if (phase === "register") return (
    <KioskRegister token={token} companyId={companyId} onRegistered={(u) => {
      setKioskUser(u);
      setPhase(u.status === "approved" ? "punch" : "pending");
    }} />
  );

  if (phase === "pending") return (
    <div style={st.container}><div style={st.box}>
      <div style={{ fontSize: "64px", textAlign: "center", marginBottom: "16px" }}>✅</div>
      <h2 style={{ ...st.title, color: "#27ae60" }}>設定が完了しました！</h2>
      <div style={{ backgroundColor: "#d5f5e3", border: "2px solid #27ae60", borderRadius: "12px", padding: "16px", margin: "16px 0", textAlign: "center" }}>
        <p style={{ color: "#1e8449", fontSize: "14px", lineHeight: "1.7", margin: 0 }}>
          <strong>{kioskUser?.name}</strong>さん<br />
          次回からQRコードを読み取るだけで打刻できます。
        </p>
      </div>
      <p style={{ textAlign: "center", fontSize: "13px", color: "#999" }}>このページは閉じてください</p>
    </div></div>
  );

  if (phase === "done") return (
    <div style={st.container}><div style={st.box}>
      <div style={{ fontSize: "64px", textAlign: "center", marginBottom: "12px" }}>{lastStatus === "出勤" ? "🟢" : "🔴"}</div>
      <h2 style={{ ...st.title, fontSize: "22px" }}>{message}</h2>
      <p style={st.time}>{new Date().toLocaleTimeString("ja-JP", { hour: "2-digit", minute: "2-digit" })}</p>
      <button style={st.backBtn} onClick={() => window.close()}>閉じる</button>
    </div></div>
  );

  if (phase === "loading") return (
    <div style={st.container}><div style={{ color: "#7f8c8d" }}>読み込み中...</div></div>
  );

  return (
    <div style={st.container}><div style={st.box}>
      <div style={{ fontSize: "40px", textAlign: "center", marginBottom: "8px" }}>👋</div>
      <h2 style={st.title}>{kioskUser?.name}さん</h2>
      <p style={st.desc}>{new Date().toLocaleDateString("ja-JP", { month: "long", day: "numeric", weekday: "short" })}</p>
      <div style={st.btnArea}>
        <button
          style={{ ...st.punchBtn, backgroundColor: lastStatus === "出勤" ? "#95a5a6" : "#27ae60", cursor: lastStatus === "出勤" ? "not-allowed" : "pointer" }}
          onClick={() => handlePunch("出勤")} disabled={punching || lastStatus === "出勤"}
        >出勤</button>
        <button
          style={{ ...st.punchBtn, backgroundColor: lastStatus !== "出勤" ? "#95a5a6" : "#e74c3c", cursor: lastStatus !== "出勤" ? "not-allowed" : "pointer" }}
          onClick={() => handlePunch("退勤")} disabled={punching || lastStatus !== "出勤"}
        >退勤</button>
      </div>
      {todayRecords.length > 0 && (
        <div style={st.records}>
          <p style={st.recTitle}>本日の打刻</p>
          {todayRecords.map(r => (
            <div key={r.id} style={st.recRow}>
              <span style={{ color: r.type === "出勤" ? "#27ae60" : "#e74c3c", fontWeight: "bold" }}>{r.type}</span>
              <span style={{ color: "#999" }}>{fmt(r.timestamp)}</span>
            </div>
          ))}
        </div>
      )}
    </div></div>
  );
}

const st = {
  container: { minHeight: "100vh", backgroundColor: "#f0f4f8", display: "flex", justifyContent: "center", alignItems: "center", padding: "20px" },
  box: { backgroundColor: "#fff", padding: "36px 28px", borderRadius: "16px", boxShadow: "0 4px 20px rgba(0,0,0,0.1)", width: "100%", maxWidth: "360px" },
  title: { textAlign: "center", color: "#2c3e50", marginBottom: "8px" },
  desc: { textAlign: "center", color: "#7f8c8d", fontSize: "14px", lineHeight: "1.6", marginBottom: "24px" },
  time: { textAlign: "center", fontSize: "32px", fontWeight: "bold", color: "#2c3e50", marginBottom: "24px" },
  btnArea: { display: "flex", gap: "16px", marginBottom: "24px" },
  punchBtn: { flex: 1, padding: "28px 0", fontSize: "22px", fontWeight: "bold", color: "#fff", border: "none", borderRadius: "12px" },
  backBtn: { width: "100%", padding: "14px", backgroundColor: "#95a5a6", color: "#fff", border: "none", borderRadius: "10px", fontSize: "16px", cursor: "pointer", marginTop: "16px" },
  records: { borderTop: "1px solid #eee", paddingTop: "16px" },
  recTitle: { color: "#999", fontSize: "13px", marginBottom: "8px" },
  recRow: { display: "flex", justifyContent: "space-between", padding: "6px 0", fontSize: "15px" },
};

export default Kiosk;
